import { buildDeliverySummary, parseMoney } from "./deliveryCalculations.js";

function formatMoney(symbol, amount) {
  return `${symbol} ${amount.toFixed(2)}`;
}

/**
 * Customer-facing name of a leg. A "pickup" leg is the car delivered to the
 * customer and a "return" leg is it collected from them — never call these
 * Pickup/Return, which mean the branch rental pick-up and return.
 */
export function legLabel(type) {
  return type === "return" ? "Collection" : "Delivery";
}

/**
 * Maps a raw availability/price API response to a UI-friendly banner
 * descriptor. Pure and framework-agnostic so it can be unit tested without
 * rendering React.
 *
 * tone: "available" | "surcharge" | "unavailable"
 */
export function describeAvailability(availability, currencySymbol, type = "pickup") {
  const label = legLabel(type);

  if (!availability || !availability.available) {
    return {
      tone: "unavailable",
      headline: `${label} not available`,
      note: availability?.message ?? `${label} service is not available for this station and time.`,
    };
  }

  const surcharge = parseMoney(availability.after_hours_surcharge);

  if (availability.after_hours && surcharge > 0) {
    return {
      tone: "surcharge",
      headline: `${label} available (after-hours)`,
      note: `After-hours surcharge of ${formatMoney(currencySymbol, surcharge)} applies.`,
    };
  }

  return {
    tone: "available",
    headline: `${label} available`,
    note: type === "return"
      ? "We can collect the car from your location."
      : "We can deliver the car to your location.",
  };
}

/**
 * Builds an ordered list of price breakdown rows plus a total row, formatting
 * money with the region's currency symbol. Optional rows (after-hours,
 * waiting) are only included when greater than zero.
 */
export function buildPriceBreakdown(price, currencySymbol, type = "pickup") {
  const summary = buildDeliverySummary(price ?? {});
  const distance = parseMoney(price?.distance_km);

  const lines = [
    { key: "distance", label: "Distance", value: `${distance.toFixed(2)} km` },
    { key: "delivery_fee", label: `${legLabel(type)} fee`, value: formatMoney(currencySymbol, summary.deliveryFee) },
  ];

  if (summary.afterHoursSurcharge > 0) {
    lines.push({
      key: "after_hours",
      label: "After-hours surcharge",
      value: formatMoney(currencySymbol, summary.afterHoursSurcharge),
    });
  }

  if (summary.waitingFee > 0) {
    lines.push({
      key: "waiting_fee",
      label: "Waiting fee",
      value: formatMoney(currencySymbol, summary.waitingFee),
    });
  }

  return {
    lines,
    total: { label: "Total", value: formatMoney(currencySymbol, summary.totalFee) },
  };
}

export function isOutOfRadius(error) {
  return Boolean(error && error.error_code === "OUT_OF_RADIUS");
}

const NAMED_PLACE_TYPES = ["establishment", "point_of_interest", "premise"];

/**
 * Builds the delivery address from a Google Places result. For hotels, malls
 * and other named places Google's `formatted_address` omits the place name
 * ("Dorsett Putrajaya" → "Precinct 3, 62000 Putrajaya"), which leaves the
 * driver without the landmark, so prefix it unless it is already there.
 */
export function composePlaceAddress(place) {
  const formatted = (place?.formatted_address ?? "").trim();
  const name = (place?.name ?? "").trim();
  const isNamedPlace = (place?.types ?? []).some((type) => NAMED_PLACE_TYPES.includes(type));

  if (!name || !isNamedPlace || formatted.toLowerCase().includes(name.toLowerCase())) {
    return formatted || name;
  }

  return formatted ? `${name}, ${formatted}` : name;
}
